import { useCompleteOrder } from '@/features/orders/hooks/useOrderMutations';
import {
  useAcceptProof,
  useCreateRefund,
  useRejectProof,
  useSettleRefund,
} from '@/features/orders/hooks/usePaymentMutations';
import { useT } from '@/i18n';
import type { OnlineOrder } from '@restaurant/api-client';
import { f7 } from 'framework7-react';
import { useRef, useState } from 'react';

/**
 * Order write-surface actions for the inspector: the lifecycle transitions
 * (accept / complete / cancel), the manual-payment proof decisions
 * (accept / reject) and the out-of-band refund settlement. Owns the mutation
 * hooks plus the confirm/prompt dialogs and toasts that wire them, so the
 * OrderInspector can stay a presentation component.
 *
 * The refund *creation* form (amount input + submit) stays in the inspector
 * because it's interwoven with render state - this hook only exposes the
 * `createRefund` mutation the form submits through, and the `*.isPending`
 * flags the render reads to disable controls.
 */
export function useOrderActions(
  order: OnlineOrder | undefined,
  // Called after a proof accept/reject succeeds. The standalone order-detail
  // page (mobile / push deep-link) passes a back-to-list navigation here so the
  // owner returns to the Orders list once they've actioned the proof; the docked
  // desktop inspector passes nothing and stays put beside the list.
  opts?: { onProofActioned?: () => void },
) {
  const tr = useT();
  const completeOrder = useCompleteOrder();
  const acceptProof = useAcceptProof();
  const rejectProof = useRejectProof();
  // Synchronous guard set right before the mutation (inside the dialog callback,
  // not at button press) so cancelling the dialog never locks it, but a confirmed
  // accept/reject can't be double-submitted by a fast double-tap.
  const proofSubmittingRef = useRef(false);
  const createRefund = useCreateRefund();
  const settleRefund = useSettleRefund();
  // The order whose accept sheet is open (null = closed). Accept opens the sheet
  // rather than firing instantly, so the owner sees — and can tune — the ETA
  // (prep + travel) before committing. The mutation itself lives in the sheet.
  const [acceptTarget, setAcceptTarget] = useState<OnlineOrder | null>(null);
  // The order whose cancel sheet is open (null = closed). Cancel opens the sheet
  // rather than a bare confirm, so a PAID online order surfaces the refund choice
  // (refund on cancel, ON by default) before committing. The mutation lives in
  // the sheet.
  const [cancelTarget, setCancelTarget] = useState<OnlineOrder | null>(null);

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const handleAccept = (o: OnlineOrder) => {
    f7.popover.close();
    setAcceptTarget(o);
  };

  // Completing a CANCELLED order / cancelling a COMPLETED one is the cash
  // correction rail (server: canCorrectFinalizedOrder). It reverses money, points
  // and the visit count, so it gets its own wording rather than the plain
  // "Mark as completed?" / "Cancel this order?" prompt.
  const handleComplete = (o: OnlineOrder) => {
    f7.popover.close();
    if (completeOrder.isPending && completeOrder.variables?.orderId === o.id) return;
    const copy =
      o.status === 'CANCELLED' ? tr.orders.action.restoreOrder : tr.orders.action.completeOrder;
    f7.dialog.confirm(copy.confirm, copy.title, () => {
      completeOrder.mutate(
        { orderId: o.id, track: { action: 'done', order: o, from: 'inspector' } },
        { onSuccess: () => toast(copy.success) },
      );
    });
  };

  // Cancel opens the sheet (refund choice for a refundable payment) rather than
  // firing a bare confirm. The sheet owns the mutation, toast and toggle.
  const handleCancel = (o: OnlineOrder) => {
    f7.popover.close();
    setCancelTarget(o);
  };

  const handleAcceptProof = () => {
    if (!order) return;
    f7.dialog.confirm(
      tr.orders.payment.acceptProof.confirm,
      tr.orders.payment.acceptProof.title,
      () => {
        if (proofSubmittingRef.current) return;
        proofSubmittingRef.current = true;
        acceptProof.mutate(
          { orderId: order.id },
          {
            onSuccess: () => opts?.onProofActioned?.(),
            onSettled: () => {
              proofSubmittingRef.current = false;
            },
          },
        );
      },
    );
  };

  const handleRejectProof = () => {
    if (!order) return;
    f7.dialog.prompt(
      tr.orders.payment.rejectProof.prompt,
      tr.orders.payment.rejectProof.title,
      (reason) => {
        if (!reason.trim()) return;
        if (proofSubmittingRef.current) return;
        proofSubmittingRef.current = true;
        rejectProof.mutate(
          { orderId: order.id, reason },
          {
            onSuccess: () => opts?.onProofActioned?.(),
            onSettled: () => {
              proofSubmittingRef.current = false;
            },
          },
        );
      },
    );
  };

  // Out-of-band refund confirmation: no amount to enter (the refund already
  // carries it) → a plain confirm, not a bounded-input panel.
  const handleSettle = (refundId: string) => {
    if (!order) return;
    if (settleRefund.isPending) return;
    f7.dialog.confirm(tr.orders.refundExtra.settlePrompt, tr.orders.refundExtra.settleTitle, () => {
      settleRefund.mutate(
        { orderId: order.id, refundId },
        { onSuccess: () => toast(tr.orders.refundExtra.settledToast) },
      );
    });
  };

  return {
    // Mutation objects the render still reads (`*.isPending`) / the refund form
    // submits through - exposed verbatim so the inspector's JSX is unchanged.
    acceptProof,
    rejectProof,
    createRefund,
    settleRefund,
    // Accept-sheet state (the sheet owns the mutation).
    acceptTarget,
    closeAcceptSheet: () => setAcceptTarget(null),
    // Cancel-sheet state (the sheet owns the mutation + refund toggle).
    cancelTarget,
    closeCancelSheet: () => setCancelTarget(null),
    // Wired callbacks.
    handleAccept,
    handleComplete,
    handleCancel,
    handleAcceptProof,
    handleRejectProof,
    handleSettle,
  };
}
